import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = [
    "input",
    "dropzone",
    "progress",
    "progressBar",
    "progressText",
    "fileInfo",
    "fileName",
    "fileSize",
    "errorMessage",
    "errorText",
    "blobInput",
    "successInfo"
  ]

  static values = {
    url: String,
    maxSize: { type: Number, default: 104857600 },
    acceptedTypes: { type: Array, default: ["audio/mpeg", "audio/mp4", "audio/x-m4a", "audio/mp3"] }
  }

  connect() {
    this.uploadInProgress = false
    this.currentUpload = null
    console.log('[AudioUpload] Controller connected', this.element)
  }

  handleDragEnter(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropzoneTarget.classList.add('border-indigo-500', 'bg-indigo-50')
  }

  handleDragOver(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    if (e.target === this.dropzoneTarget) {
      this.dropzoneTarget.classList.remove('border-indigo-500', 'bg-indigo-50')
    }
  }

  handleDrop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dropzoneTarget.classList.remove('border-indigo-500', 'bg-indigo-50')

    const files = e.dataTransfer.files
    if (files.length > 0) {
      this.processFile(files[0])
    }
  }

  openFileDialog(e) {
    e.preventDefault()
    this.inputTarget.click()
  }

  handleFileSelect(e) {
    const file = e.target.files[0]
    if (file) {
      this.processFile(file)
    }
  }

  processFile(file) {
    if (!this.acceptedTypesValue.includes(file.type)) {
      this.showError('Please select an MP3 or M4A audio file')
      return
    }

    if (file.size > this.maxSizeValue) {
      this.showError(`File size exceeds maximum of ${this.formatFileSize(this.maxSizeValue)}`)
      return
    }

    this.clearError()
    this.displayFileInfo(file)
    this.uploadFile(file)
  }

  displayFileInfo(file) {
    console.log('[AudioUpload] Displaying file info:', file.name, file.size)
    this.fileNameTarget.textContent = file.name
    this.fileSizeTarget.textContent = this.formatFileSize(file.size)
    this.fileInfoTarget.classList.remove('hidden')
    this.dropzoneTarget.classList.add('hidden')
    console.log('[AudioUpload] fileInfo visible:', !this.fileInfoTarget.classList.contains('hidden'))
  }

  uploadFile(file) {
    this.uploadInProgress = true
    this.progressTarget.classList.remove('hidden')

    const upload = new DirectUpload(file, this.urlValue, this)
    this.currentUpload = upload

    upload.create((error, blob) => {
      if (error) {
        this.showError(`Upload failed: ${error}`)
        this.resetUpload()
      } else {
        this.blobInputTarget.value = blob.signed_id
        this.uploadComplete()
      }
    })
  }

  directUploadWillStoreFileWithXHR(request) {
    request.upload.addEventListener("progress", (event) => {
      const percentage = Math.round((event.loaded / event.total) * 100)
      this.progressBarTarget.style.width = `${percentage}%`
      this.progressTextTarget.textContent = `Uploading... ${percentage}%`
    })
  }

  uploadComplete() {
    console.log('[AudioUpload] Upload complete! blob_signed_id:', this.blobInputTarget.value)
    this.uploadInProgress = false
    this.progressBarTarget.classList.remove('bg-indigo-600')
    this.progressBarTarget.classList.add('bg-green-600')
    this.progressTextTarget.textContent = 'Upload complete!'
    console.log('[AudioUpload] fileInfo still visible:', !this.fileInfoTarget.classList.contains('hidden'))

    if (this.hasSuccessInfoTarget) {
      this.successInfoTarget.classList.remove('hidden')
    }

    this.dispatch('complete', { detail: { blobId: this.blobInputTarget.value } })

    // Try to find transcripts controller (for edit page)
    const transcriptsController = this.element.closest('[data-controller*="transcripts"]')
    if (transcriptsController) {
      // Try 'transcripts' controller first (edit page)
      let controller = this.application.getControllerForElementAndIdentifier(
        transcriptsController,
        'transcripts'
      )
      // If not found, try 'invite-transcripts' controller (invite page)
      if (!controller) {
        controller = this.application.getControllerForElementAndIdentifier(
          transcriptsController,
          'invite-transcripts'
        )
      }
      if (controller && typeof controller.enableUploadSubmit === 'function') {
        controller.enableUploadSubmit()
      }
    }
  }

  removeFile(e) {
    e.preventDefault()
    this.resetUpload()
    this.blobInputTarget.value = ''

    const transcriptsController = this.element.closest('[data-controller*="transcripts"]')
    if (transcriptsController) {
      const controller = this.application.getControllerForElementAndIdentifier(
        transcriptsController,
        'transcripts'
      )
      if (controller && typeof controller.disableUploadSubmit === 'function') {
        controller.disableUploadSubmit()
      }
    }
  }

  resetUpload() {
    this.uploadInProgress = false
    this.currentUpload = null
    this.fileInfoTarget.classList.add('hidden')
    this.progressTarget.classList.add('hidden')
    this.dropzoneTarget.classList.remove('hidden')
    this.progressBarTarget.style.width = '0%'
    this.progressBarTarget.classList.remove('bg-green-600')
    this.progressBarTarget.classList.add('bg-indigo-600')
    this.progressTextTarget.textContent = 'Uploading... 0%'
    this.inputTarget.value = ''
    if (this.hasSuccessInfoTarget) {
      this.successInfoTarget.classList.add('hidden')
    }
  }

  showError(message) {
    this.errorTextTarget.textContent = message
    this.errorMessageTarget.classList.remove('hidden')
  }

  clearError() {
    this.errorMessageTarget.classList.add('hidden')
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  disconnect() {
    if (this.uploadInProgress && this.currentUpload?.xhr) {
      this.currentUpload.xhr.abort()
    }
  }
}
